import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import TablePeo from '../../../Components/TablePeo';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  downloadPickListsPdf,
  generateVendorBatches,
  getOrderStatuses,
  listManagementOrders,
} from '../../../endpoints/chapterSupplies';
import type { ManagementOrderSummary } from '../../../types/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const formatBatchType = (batchType?: string) => {
  switch (batchType) {
    case 'chapter_pins':
      return 'Chapter pins';
    case 'state_pins':
      return 'State pins';
    default:
      return batchType || '—';
  }
};

type Row = ManagementOrderSummary & {
  id: string;
  order_display: string;
  fulfillment_display: string;
};

const headCells = [
  {
    id: 'order_display',
    firstCell: true,
    label: 'Order',
    width: '15%',
    sortable: false,
  },
  {
    id: 'placed_at',
    label: 'Placed',
    width: '18%',
    sortable: false,
    format: (v: unknown) => (v ? new Date(String(v)).toLocaleString() : ''),
  },
  { id: 'order_status_name', label: 'Status', width: '14%', sortable: false },
  {
    id: 'fulfillment_display',
    label: 'Fulfillment',
    width: '14%',
    sortable: false,
  },
  {
    id: 'purchaser_display_name',
    label: 'Purchaser',
    width: '18%',
    sortable: false,
  },
  { id: 'ship_to_name', label: 'Ship to', width: '15%', sortable: false },
  {
    id: 'total',
    label: 'Total',
    width: '12%',
    sortable: false,
    format: (v: unknown) => formatPrice(Number(v)),
  },
];

export default function ManagementOrdersList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('placed_at');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchSuccess, setBatchSuccess] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [internalFulfillmentOpenOnly, setInternalFulfillmentOpenOnly] =
    useState(false);

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      400
    );
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const purchaserQ = useMemo(() => {
    if (debouncedSearch.length === 0) return undefined;
    if (debouncedSearch.length >= 2) return debouncedSearch;
    return undefined;
  }, [debouncedSearch]);

  useEffect(() => {
    setPage(0);
    setSelectedRows([]);
  }, [statusFilter, purchaserQ, internalFulfillmentOpenOnly]);

  const limit = rowsPerPage;
  const offset = page * rowsPerPage;

  const { data, isFetching, error } = useApiGet(
    [
      'chapter-supplies-management-orders',
      limit,
      offset,
      statusFilter,
      purchaserQ ?? '',
      internalFulfillmentOpenOnly,
    ],
    () =>
      listManagementOrders({
        limit,
        offset,
        order_status_id: statusFilter || undefined,
        q: purchaserQ,
        internal_fulfillment_open: internalFulfillmentOpenOnly || undefined,
      }),
    { enabled: true, retry: 1 }
  );

  const { data: orderStatuses = [] } = useApiGet(
    ['chapter-supplies-order-statuses'],
    getOrderStatuses,
    { retry: 1 }
  );

  const tableRows: Row[] = useMemo(
    () =>
      (data?.data ?? []).map((o) => ({
        ...o,
        id: o.order_id,
        order_display:
          o.order_number != null ? `#${o.order_number}` : o.order_id,
        fulfillment_display: (() => {
          const base = o.is_vendor_direct
            ? o.vendor_batches && o.vendor_batches.length > 0
              ? `${
                  o.vendor_batches.every(
                    (batch) => batch.batch_status === 'sent'
                  )
                    ? 'Vendor sent'
                    : 'Vendor batched'
                }: ${o.vendor_batches
                  .map((batch) => formatBatchType(batch.batch_type))
                  .join(', ')}`
              : 'Vendor direct'
            : 'Internal';
          if (o.is_vendor_direct && o.internal_fulfillment_open) {
            return `${base} · Org ship pending`;
          }
          return base;
        })(),
      })),
    [data?.data]
  );

  const handleRowClick = (_event: React.MouseEvent, rowId: string | number) => {
    navigate(`/chapter-supplies-management/orders/${rowId}`);
  };

  const handlePickLists = async () => {
    const ids = selectedRows.map(String).filter(Boolean);
    if (ids.length === 0) return;
    const selectedVendorDirect = tableRows.filter(
      (row) => ids.includes(row.order_id) && row.is_vendor_direct
    );
    if (selectedVendorDirect.length > 0) {
      setPdfError(
        'Vendor-direct orders bypass internal fulfillment and cannot be included in pick lists.'
      );
      return;
    }
    setPdfError(null);
    setPdfLoading(true);
    try {
      const blob = await downloadPickListsPdf(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pick-lists-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(
        e instanceof Error ? e.message : 'Could not generate pick lists PDF.'
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const handleGenerateVendorBatches = async () => {
    setBatchError(null);
    setBatchSuccess(null);
    setBatchLoading(true);
    try {
      const resp = await generateVendorBatches();
      setBatchSuccess(
        resp.orders_processed > 0
          ? `Generated ${resp.batches.length} vendor batch${resp.batches.length === 1 ? '' : 'es'} for ${resp.orders_processed} order${resp.orders_processed === 1 ? '' : 's'}.`
          : 'No eligible vendor-direct orders were waiting to be batched.'
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-management-orders'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches'],
        }),
      ]);
    } catch (e) {
      setBatchError(
        e instanceof Error ? e.message : 'Could not generate vendor batches.'
      );
    } finally {
      setBatchLoading(false);
    }
  };

  const searchHint =
    debouncedSearch.length === 1
      ? 'Enter at least 2 characters to search by purchaser name or email.'
      : null;

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Customer orders"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management')}
            >
              Back
            </Button>
          }
          rightDom={undefined}
          subtitle="Storefront orders — filter by status, search purchasers, select rows to print pick lists."
        />
      </Grid>
      <Grid item xs={12}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load orders.
          </Alert>
        )}
        {pdfError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setPdfError(null)}
          >
            {pdfError}
          </Alert>
        )}
        {batchError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setBatchError(null)}
          >
            {batchError}
          </Alert>
        )}
        {batchSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setBatchSuccess(null)}
          >
            {batchSuccess}
          </Alert>
        )}
        <TablePeo
          data={tableRows}
          isFetching={isFetching}
          headCells={headCells}
          handleRowClick={handleRowClick}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          emptyMessage="No storefront orders match your filters."
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          remoteTotalCount={data?.total}
          toolbar={
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="cs-order-status-filter">Status</InputLabel>
                <Select
                  labelId="cs-order-status-filter"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(String(e.target.value))}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {orderStatuses.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search purchaser"
                placeholder="Name or email (min 2 characters)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                sx={{ minWidth: 260 }}
              />
              <FormControlLabel
                sx={{ ml: { xs: 0, md: 1 } }}
                control={
                  <Switch
                    checked={internalFulfillmentOpenOnly}
                    onChange={(_, checked) =>
                      setInternalFulfillmentOpenOnly(checked)
                    }
                    color="primary"
                  />
                }
                label="Org fulfillment open"
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                onClick={() =>
                  navigate('/chapter-supplies-management/vendor-batches')
                }
              >
                Vendor batches
              </Button>
              <Button
                variant="outlined"
                disabled={batchLoading}
                onClick={() => void handleGenerateVendorBatches()}
              >
                {batchLoading ? 'Generating…' : 'Generate vendor batches'}
              </Button>
              <Button
                variant="contained"
                disabled={selectedRows.length === 0 || pdfLoading}
                onClick={() => void handlePickLists()}
              >
                {pdfLoading ? 'Generating…' : 'Create pick lists'}
              </Button>
            </Stack>
          }
        />
        {searchHint && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            {searchHint}
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}
